import { Group } from "@fairfleet/geotab";
import {
  FUEL_TYPE_GROUPS,
  FuelType,
  GEOTAB_POWERTRAIN_GROUP_FUEL_TYPES,
} from "@/constants/FuelTypeGroups";
import { VEHICLE_TYPE_GROUP_NAME, VEHICLE_TYPE_PARENT_NAME } from "@/constants/VehicleTypeGroup";
import { GroupIndex, groupAncestry, isGeotabSystemGroup } from "@/utilities/groupIndex";

/** True when the device belongs to one of the groups or to one of their descendants. */
export function isDeviceInAnyGroup(
  device: { groups?: { id: string }[] },
  groupIds: string[],
  index: GroupIndex,
): boolean {
  if (groupIds.length === 0) return true;
  const wanted = new Set(groupIds);
  return (device.groups ?? []).some(group =>
    groupAncestry(group.id, index).some(ancestor => wanted.has(ancestor.id)),
  );
}

/**
 * Resolves the drive type from the device's groups (direct or inherited). The customer's own
 * groups ("Elektro", "Diesel", …) decide; Geotab's built-in powertrain groups carry the same
 * names and may classify a bus differently (a BEV as plug-in hybrid with a Diesel entry), so
 * they only count when no customer group applies. Undefined when neither is found.
 */
export function resolveFuelType(
  device: { groups?: { id: string }[] },
  index: GroupIndex,
): FuelType | undefined {
  const names = new Map(FUEL_TYPE_GROUPS.map(g => [g.groupName.toLocaleLowerCase("de-DE"), g.fuelType]));
  const ancestries = (device.groups ?? []).map(group => groupAncestry(group.id, index));
  for (const ancestry of ancestries) {
    for (const ancestor of ancestry) {
      if (isGeotabSystemGroup(ancestor.id)) continue;
      const fuelType = names.get((ancestor.name ?? "").trim().toLocaleLowerCase("de-DE"));
      if (fuelType) return fuelType;
    }
  }
  for (const ancestry of ancestries) {
    for (const ancestor of ancestry) {
      const fuelType = GEOTAB_POWERTRAIN_GROUP_FUEL_TYPES[ancestor.id];
      if (fuelType) return fuelType;
    }
  }
  return undefined;
}

/**
 * Geotab allows several groups with the same name (one "AK - Flensburg" per region). For the
 * pickers, each such group gets its parent's name as a hint, so the options are distinguishable;
 * groups with a unique name get none.
 */
export function groupParentHints(groups: Group[], index: GroupIndex): Map<string, string> {
  const byName = new Map<string, Group[]>();
  for (const group of groups) {
    if (!group.name) continue;
    byName.set(group.name, [...(byName.get(group.name) ?? []), group]);
  }
  const hints = new Map<string, string>();
  for (const sameName of byName.values()) {
    if (sameName.length < 2) continue;
    for (const group of sameName) {
      const parent = index.parentById.get(group.id);
      hints.set(group.id, (parent && index.byId.get(parent)?.name) || "");
    }
  }
  return hints;
}

const GROUP_NAME_COLLATOR = new Intl.Collator("de", { numeric: true, sensitivity: "base" });

/** Alphabetical order of group names as a German reader expects it, numbers by value ("8-11m" before "18m"). */
export function compareGroupNames(a: { name?: string }, b: { name?: string }): number {
  return GROUP_NAME_COLLATOR.compare(a.name ?? "", b.name ?? "");
}

/** The vehicle-type group ("Typ" below "Attribute"); undefined when the user's group list has none. */
export function findVehicleTypeGroup(index: GroupIndex): Group | undefined {
  const candidates = [...index.byId.values()].filter(group => group.name === VEHICLE_TYPE_GROUP_NAME);
  const belowAttribute = candidates.find(group => {
    const parent = index.parentById.get(group.id);
    return !!parent && index.byId.get(parent)?.name === VEHICLE_TYPE_PARENT_NAME;
  });
  return belowAttribute ?? candidates[0];
}

/**
 * True when one of the device's groups, or one of their ancestors, is in `groupIds`. Works on
 * ids alone: a customs auditor's group list does not contain the type groups, the device's own
 * group ids still match.
 */
export function deviceHasAnyGroup(
  device: { groups?: { id: string }[] },
  groupIds: ReadonlySet<string>,
  index: GroupIndex,
): boolean {
  if (groupIds.size === 0) return false;
  for (const group of device.groups ?? []) {
    const seen = new Set<string>();
    let current: string | undefined = group.id;
    while (current && !seen.has(current)) {
      if (groupIds.has(current)) return true;
      seen.add(current);
      current = index.parentById.get(current);
    }
  }
  return false;
}
